import { Pause, Play } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '@aeris/ui'

/**
 * O áudio do WhatsApp na bolha, tocando como no WhatsApp — 03/10.
 *
 * A loja: *"ele toca, mas o marcador de tempo fica 0:00/0:00, daí acham que
 * não tem nada"*. O tocador nativo do navegador não sabe a duração de um
 * `ogg/opus` antes de baixar o arquivo inteiro (e às vezes nem depois), e mostra
 * zero. A duração está na mensagem — o WhatsApp a manda (`media.seconds`) —, e
 * é ela que este tocador mostra até o navegador saber a sua.
 *
 * Um áudio por vez: dar play num pausa o que estava tocando, como no celular.
 */

let playing: HTMLAudioElement | null = null

const SPEEDS = [1, 1.5, 2] as const

/** `0:07`, `1:15`. */
const clock = (seconds: number): string => {
  const whole = Math.max(Math.floor(seconds), 0)
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`
}

export const AudioPlayer = ({
  src,
  seconds,
  onError,
  outgoing = false,
}: {
  src: string
  /** A duração que o WhatsApp mandou; nula em áudio antigo. */
  seconds: number | null
  onError: () => void
  outgoing?: boolean
}): ReactNode => {
  const audio = useRef<HTMLAudioElement>(null)
  const [isPlaying, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [known, setKnown] = useState<number | null>(null)
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1)

  // A do navegador só quando ela é um número de verdade; senão, a do WhatsApp.
  const duration = known ?? seconds ?? 0

  useEffect(
    () => () => {
      if (playing === audio.current) playing = null
    },
    []
  )

  const toggle = (): void => {
    const element = audio.current
    if (element === null) return
    if (element.paused) {
      if (playing !== null && playing !== element) playing.pause()
      playing = element
      element.playbackRate = speed
      void element.play().catch(() => {
        setPlaying(false)
      })
    } else {
      element.pause()
    }
  }

  const readDuration = (): void => {
    const value = audio.current?.duration
    if (value !== undefined && Number.isFinite(value) && value > 0) {
      setKnown(value)
    }
  }

  return (
    <span className="flex w-64 max-w-full items-center gap-2">
      <audio
        ref={audio}
        preload="none"
        src={src}
        onError={onError}
        onPlay={() => {
          setPlaying(true)
        }}
        onPause={() => {
          setPlaying(false)
        }}
        onEnded={() => {
          setPlaying(false)
          setPosition(0)
        }}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onTimeUpdate={() => {
          setPosition(audio.current?.currentTime ?? 0)
        }}
      >
        <track kind="captions" />
      </audio>

      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? 'Pausar o áudio' : 'Tocar o áudio'}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-full)',
          'bg-(--brand-primary) text-(--text-on-brand) hover:bg-(--brand-primary-hover)',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)'
        )}
      >
        {isPlaying ? (
          <Pause aria-hidden="true" className="h-4 w-4" />
        ) : (
          <Play aria-hidden="true" className="ml-0.5 h-4 w-4" />
        )}
      </button>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <input
          type="range"
          aria-label="Posição do áudio"
          min={0}
          max={duration > 0 ? duration : 1}
          step={0.1}
          value={Math.min(position, duration > 0 ? duration : 1)}
          onChange={(event) => {
            const next = Number(event.target.value)
            setPosition(next)
            if (audio.current !== null) audio.current.currentTime = next
          }}
          className={cn(
            'h-1.5 w-full cursor-pointer',
            outgoing ? 'accent-(--brand-primary)' : 'accent-(--text-secondary)'
          )}
        />
        <span className="flex justify-between text-(length:--text-xs) text-(--text-muted) tabular-nums">
          {/*
           * Parado no começo, a duração; tocando, onde está. É o que o
           * WhatsApp faz, e é o número que diz "tem áudio aqui".
           */}
          <span>
            {isPlaying || position > 0 ? clock(position) : clock(duration)}
          </span>
          <button
            type="button"
            aria-label={`Velocidade ${String(speed)}x`}
            className="rounded-(--radius-sm) px-1 font-(--weight-medium) text-(--text-secondary) hover:bg-(--surface-hover)"
            onClick={() => {
              const next =
                SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length] ?? 1
              setSpeed(next)
              if (audio.current !== null) audio.current.playbackRate = next
            }}
          >
            {`${String(speed).replace('.', ',')}x`}
          </button>
        </span>
      </span>
    </span>
  )
}
